/**
 * NextActionCard — Non-HR UX layer (HUMANAI_UX_NONHR.md §3, หลักการ 2).
 *
 * "นำทาง ไม่ใช่ให้เลือกเอง" — operator (เจ้าของ/แอดมิน) ที่ใช้เดือนละครั้ง
 * ลืมว่าต้องทำอะไร. dashboard บอก "สิ่งที่ต้องทำตอนนี้" เป็นการ์ดเด่นการ์ดเดียว
 * + ปุ่มเข้า flow ตรงๆ แทนที่จะให้เพ่งหาเองในกองเมนู.
 *
 * tone:
 *   warn    — 🟡 มีงานค้างต้องเคลียร์ (amber)
 *   default — งานปกติที่ถึงคิว (brand)
 *
 * Portable — CSS class กลาง + lucide เท่านั้น (reuse ข้าม product).
 */
import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export function NextActionCard({
  icon: Icon,
  title,
  detail,
  action,
  tone = 'default',
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  detail?: ReactNode;
  action: { label: string; to?: string; onClick?: () => void };
  tone?: 'default' | 'warn';
}) {
  const accent =
    tone === 'warn'
      ? { wrap: 'border-amber-300 bg-amber-50', badge: 'bg-amber-400 text-white', title: 'text-amber-900', detail: 'text-amber-800' }
      : { wrap: 'border-brand-200 bg-brand-50', badge: 'bg-brand-600 text-white', title: 'text-brand-900', detail: 'text-brand-800' };

  return (
    <div className={`rounded-xl border p-4 flex items-center gap-3 ${accent.wrap}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accent.badge}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${accent.title}`}>{title}</div>
        {detail && <div className={`text-sm ${accent.detail}`}>{detail}</div>}
      </div>
      {action.to ? (
        <Link to={action.to} className="btn-cta shrink-0">{action.label}</Link>
      ) : (
        <button type="button" onClick={action.onClick} className="btn-cta shrink-0">{action.label}</button>
      )}
    </div>
  );
}
